import { FC, useEffect, useState } from 'react';
import { useCreate, useUpdate, useResourceParams } from '@refinedev/core';

import { ATTACKIQ_ID } from '@attackiq/constants';

import SavesOnTheFlyAlert from '../../../../components/SavesOnTheFlyAlert';
import { ScenarioUnifiedMitigation, UnifiedMitigation, UnifiedMitigationType } from '../../../../types';

import {
  useScenarioUnifiedMitigations,
  DETECTION_RULE_TYPE_IDS,
  MITIGATION_TYPE_IDS
} from './../../../../hooks/useScenarioUnifiedMitigations';
import { useUnifiedMitigations } from './../../../../hooks/useUnifiedMitigations';
import { useUnifiedMitigationTypes } from './../../../../hooks/useUnifiedMitigationTypes';
import { ScenarioUnifiedMitigationsList, UnifiedMitigationDialog, UnifiedMitigationsAutocomplete } from './components';

interface ScenarioUnifiedMitigationsTabProps {
  show_mitigations?: boolean;
  show_detection_rules?: boolean;
}

const ScenarioUnifiedMitigationsTab: FC<ScenarioUnifiedMitigationsTabProps> = ({
  show_mitigations = false,
  show_detection_rules = false
}) => {
  const { id: scenarioId } = useResourceParams();

  const { scenarioMitigationsQuery } = useScenarioUnifiedMitigations(
    scenarioId?.toString(),
    show_mitigations,
    show_detection_rules
  );
  const { mitigationsQuery } = useUnifiedMitigations(show_mitigations, show_detection_rules);
  const { unifiedMitigationTypesQuery } = useUnifiedMitigationTypes();

  const [selectedMitigations, setSelectedMitigations] = useState<ScenarioUnifiedMitigation[]>([]);
  const [dialogMitigation, setDialogMitigation] = useState<UnifiedMitigation | null>(null);

  const { mutateAsync: createMitigation } = useCreate<ScenarioUnifiedMitigation>();
  const { mutateAsync: updateMitigationsOrder } = useUpdate<ScenarioUnifiedMitigation[]>();

  useEffect(() => {
    if (
      scenarioMitigationsQuery.data &&
      typeof scenarioMitigationsQuery.data === 'object' &&
      'data' in scenarioMitigationsQuery.data
    ) {
      setSelectedMitigations((scenarioMitigationsQuery.data as { data: unknown }).data as ScenarioUnifiedMitigation[]);
    } else if (Array.isArray(scenarioMitigationsQuery.data)) {
      setSelectedMitigations(scenarioMitigationsQuery.data as ScenarioUnifiedMitigation[]);
    }
  }, [scenarioMitigationsQuery.data]);

  const handleAddMitigation = async (mitigation: UnifiedMitigation) => {
    await createMitigation({
      resource: 'scenario_unified_mitigations',
      values: {
        scenario: scenarioId,
        unifiedmitigation: mitigation.id,
        company: ATTACKIQ_ID
      }
    });
    scenarioMitigationsQuery.refetch();
  };

  const handlePersistOrder = async (mitigations: ScenarioUnifiedMitigation[]) => {
    await Promise.all(
      mitigations.map((m, index) =>
        updateMitigationsOrder({
          resource: 'scenario_unified_mitigations',
          id: m.id || '',
          values: { ...m, order: index },
          meta: { method: 'PUT' }
        })
      )
    );
    scenarioMitigationsQuery.refetch();
  };

  return (
    <>
      <SavesOnTheFlyAlert />

      <UnifiedMitigationsAutocomplete
        allMitigations={mitigationsQuery.data?.data || []}
        selectedScenarioMitigations={selectedMitigations}
        onSelect={handleAddMitigation}
      />

      {show_mitigations && unifiedMitigationTypesQuery.data?.data
        ? MITIGATION_TYPE_IDS.map(typeId => {
            const type = unifiedMitigationTypesQuery.data.data.find((t: UnifiedMitigationType) => t.id === typeId);
            if (!type) return null;

            const items = selectedMitigations.filter(m => m.unifiedmitigationtype?.toString() === typeId.toString());

            return (
              <div key={type.id} style={{ marginBottom: 24 }}>
                <h3>{type.name}</h3>
                <ScenarioUnifiedMitigationsList
                  items={items}
                  setItems={newItems => {
                    setSelectedMitigations(prev =>
                      prev.filter(m => m.unifiedmitigationtype?.toString() !== typeId.toString()).concat(newItems)
                    );
                  }}
                  openDialog={setDialogMitigation}
                  persistOrder={handlePersistOrder}
                  typeId={type.id.toString()}
                  isLoading={scenarioMitigationsQuery.isLoading}
                />
              </div>
            );
          })
        : null}

      {show_detection_rules && unifiedMitigationTypesQuery.data?.data
        ? unifiedMitigationTypesQuery.data.data
            .filter((type: UnifiedMitigationType) => DETECTION_RULE_TYPE_IDS.includes(type.id))
            .map((type: UnifiedMitigationType) => {
              const items = selectedMitigations.filter(m => m.unifiedmitigationtype?.toString() === type.id.toString());
              return (
                <div key={type.id} style={{ marginBottom: 24 }}>
                  <h3>{type.name}</h3>
                  <ScenarioUnifiedMitigationsList
                    items={items}
                    setItems={newItems => {
                      setSelectedMitigations(prev =>
                        prev.filter(m => m.unifiedmitigationtype?.toString() !== type.id.toString()).concat(newItems)
                      );
                    }}
                    openDialog={setDialogMitigation}
                    persistOrder={handlePersistOrder}
                    typeId={type.id.toString()}
                    isLoading={scenarioMitigationsQuery.isLoading}
                  />
                </div>
              );
            })
        : null}

      <UnifiedMitigationDialog
        open={Boolean(dialogMitigation)}
        onClose={() => setDialogMitigation(null)}
        mitigation={dialogMitigation}
      />
    </>
  );
};

export default ScenarioUnifiedMitigationsTab;
